import React, { useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../lib/apiConfig';
import { apiFetch } from '../../lib/apiFetch';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ImportError {
  row: number;
  field: string;
  reason: string;
}

interface ImportResult {
  valid: true;
  created: number;
}

interface ImportFailure {
  valid: false;
  errors: ImportError[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// ─── Styled Components ───────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: ${p => (p.$isOpen ? 'flex' : 'none')};
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 3000;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const Sheet = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: ${slideUp} 0.25s ease;
  overflow: hidden;

  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 20px 20px 0 0;
    max-height: 92vh;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 24px 20px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
`;

const TitleBlock = styled.div``;

const Title = styled.h2`
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: #64748b;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: #475569;
    background: #f1f5f9;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TemplateSection = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 500px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TemplateText = styled.div``;

const TemplateTitle = styled.p`
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const TemplateDesc = styled.p`
  margin: 0;
  font-size: 12px;
  color: #64748b;
`;

const DownloadButton = styled.a`
  background: white;
  border: 1.5px solid #e2e8f0;
  color: #334155;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.15s;

  &:hover {
    border-color: var(--accent, #3b82f6);
    color: var(--accent, #3b82f6);
    background: #eff6ff;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const UploadZone = styled.div<{ $hasFile: boolean; $hasError: boolean; $isDragging: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 2px dashed ${p => p.$hasError ? '#ef4444' : (p.$isDragging || p.$hasFile) ? 'var(--accent, #3b82f6)' : '#cbd5e1'};
  border-radius: 12px;
  padding: 32px 24px;
  cursor: ${p => p.$hasFile ? 'default' : 'pointer'};
  background: ${p => p.$isDragging ? '#dbeafe' : p.$hasFile ? '#eff6ff' : '#f8fafc'};
  transition: all 0.2s;
  text-align: center;

  &:hover {
    border-color: var(--accent, #3b82f6);
    background: #eff6ff;
  }
`;

const UploadIcon = styled.div<{ $hasFile: boolean }>`
  color: ${p => p.$hasFile ? 'var(--accent, #3b82f6)' : '#94a3b8'};
  svg { width: 40px; height: 40px; }
`;

const UploadText = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
`;

const UploadHint = styled.p`
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
`;

const FileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1.5px solid var(--accent, #3b82f6);
  color: var(--accent, #3b82f6);
  border-radius: 8px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
`;

const RemoveFile = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 0;
  line-height: 1;
  display: flex;
  align-items: center;

  &:hover { color: #ef4444; }

  svg { width: 14px; height: 14px; }
`;

const ErrorBox = styled.div`
  border: 1.5px solid #fecaca;
  border-radius: 12px;
  overflow: hidden;
`;

const ErrorBoxHeader = styled.div`
  background: #fef2f2;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg { width: 18px; height: 18px; color: #ef4444; flex-shrink: 0; }
`;

const ErrorBoxTitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #dc2626;
`;

const ErrorTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 16px;
  background: #fff7f7;
  border-bottom: 1px solid #fecaca;
  color: #7f1d1d;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Td = styled.td`
  padding: 8px 16px;
  border-bottom: 1px solid #fee2e2;
  color: #374151;
  vertical-align: top;

  &:last-child { color: #dc2626; }
`;

const Tr = styled.tr`
  &:last-child td { border-bottom: none; }
  &:hover td { background: #fff5f5; }
`;

const SuccessBox = styled.div`
  background: #f0fdf4;
  border: 1.5px solid #bbf7d0;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 56px;
  height: 56px;
  background: #dcfce7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #16a34a;
  svg { width: 28px; height: 28px; }
`;

const SuccessTitle = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #15803d;
`;

const SuccessDesc = styled.p`
  margin: 0;
  font-size: 14px;
  color: #166534;
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
`;

const CancelBtn = styled.button`
  background: white;
  border: 1.5px solid #e2e8f0;
  color: #475569;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const SubmitBtn = styled.button`
  background: linear-gradient(135deg, var(--accent, #3b82f6) 0%, var(--accent-dark, #2563eb) 100%);
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg { width: 18px; height: 18px; }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const ImportContactsModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const { session } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [importErrors, setImportErrors] = useState<ImportError[] | null>(null);
  const [successCount, setSuccessCount] = useState<number | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const reset = () => {
    setFile(null);
    setImportErrors(null);
    setSuccessCount(null);
    setGlobalError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const applyFile = (f: File | null) => {
    setImportErrors(null);
    setSuccessCount(null);
    setGlobalError(null);
    setFile(f);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyFile(e.target.files?.[0] ?? null);
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    reset();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (!dropped) return;
    if (!dropped.name.endsWith('.xlsx') && dropped.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      setGlobalError('Formato inválido. Apenas arquivos .xlsx são aceitos.');
      return;
    }
    applyFile(dropped);
  };

  const handleSubmit = async () => {
    if (!file || !session?.access_token) return;

    setUploading(true);
    setImportErrors(null);
    setSuccessCount(null);
    setGlobalError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors && Array.isArray(data.errors)) {
          setImportErrors(data.errors);
        } else {
          setGlobalError(data.error || 'Erro ao processar importação');
        }
        return;
      }

      const result = data as ImportResult;
      setSuccessCount(result.created);
      setFile(null);
      onSuccess();
    } catch (err) {
      console.error('Erro na importação:', err);
      setGlobalError('Erro de rede ao enviar o arquivo. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const handleTemplateDownload = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (!session?.access_token) return;

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/import/template`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_contatos.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar template:', err);
    }
  };

  return (
    <Overlay $isOpen={isOpen} onClick={handleClose}>
      <Sheet onClick={e => e.stopPropagation()}>
        {/* Header */}
        <Header>
          <TitleBlock>
            <Title>Importar Contatos</Title>
            <Subtitle>Faça upload de um arquivo Excel (.xlsx) com até 1.000 contatos</Subtitle>
          </TitleBlock>
          <CloseButton onClick={handleClose} aria-label="Fechar">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="22" height="22">
              <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </CloseButton>
        </Header>

        {/* Body */}
        <Body>
          {/* Template download */}
          <TemplateSection>
            <TemplateText>
              <TemplateTitle>Baixe o modelo de planilha</TemplateTitle>
              <TemplateDesc>Use o modelo oficial para garantir que as colunas estejam no formato correto.</TemplateDesc>
            </TemplateText>
            <DownloadButton href="#" onClick={handleTemplateDownload}>
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M16 12l-4 4m0 0l-4-4m4 4V4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Baixar Modelo
            </DownloadButton>
          </TemplateSection>

          {/* File upload zone — only show when not succeeded yet */}
          {successCount === null && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <UploadZone
                $hasFile={!!file}
                $hasError={!!importErrors}
                $isDragging={isDragging}
                onClick={() => !file && fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <UploadIcon $hasFile={!!file}>
                  {file ? (
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </UploadIcon>
                {file ? (
                  <>
                    <FileChip>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {file.name}
                      <RemoveFile onClick={handleRemoveFile} title="Remover arquivo">
                        <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </RemoveFile>
                    </FileChip>
                    <UploadHint>Clique em "Importar" para continuar</UploadHint>
                  </>
                ) : (
                  <>
                    <UploadText>{isDragging ? 'Solte o arquivo aqui' : 'Arraste o arquivo aqui ou clique para selecionar'}</UploadText>
                    <UploadHint>Somente arquivos .xlsx • Máximo 1.000 linhas</UploadHint>
                  </>
                )}
              </UploadZone>
            </>
          )}

          {/* Global error */}
          {globalError && (
            <ErrorBox>
              <ErrorBoxHeader>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <ErrorBoxTitle>{globalError}</ErrorBoxTitle>
              </ErrorBoxHeader>
            </ErrorBox>
          )}

          {/* Row-level errors */}
          {importErrors && importErrors.length > 0 && (
            <ErrorBox>
              <ErrorBoxHeader>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <ErrorBoxTitle>{importErrors.length} erro{importErrors.length !== 1 ? 's' : ''} encontrado{importErrors.length !== 1 ? 's' : ''} — nenhum contato foi importado</ErrorBoxTitle>
              </ErrorBoxHeader>
              <ErrorTable>
                <thead>
                  <tr>
                    <Th>Linha</Th>
                    <Th>Campo</Th>
                    <Th>Motivo</Th>
                  </tr>
                </thead>
                <tbody>
                  {importErrors.map((err, i) => (
                    <Tr key={i}>
                      <Td><strong>{err.row}</strong></Td>
                      <Td>{err.field}</Td>
                      <Td>{err.reason}</Td>
                    </Tr>
                  ))}
                </tbody>
              </ErrorTable>
            </ErrorBox>
          )}

          {/* Success */}
          {successCount !== null && (
            <SuccessBox>
              <SuccessIcon>
                <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </SuccessIcon>
              <SuccessTitle>Importação concluída!</SuccessTitle>
              <SuccessDesc>
                {successCount} contato{successCount !== 1 ? 's' : ''} importado{successCount !== 1 ? 's' : ''} com sucesso.
              </SuccessDesc>
            </SuccessBox>
          )}
        </Body>

        {/* Footer */}
        <Footer>
          <CancelBtn onClick={handleClose}>
            {successCount !== null ? 'Fechar' : 'Cancelar'}
          </CancelBtn>
          {successCount === null && (
            <SubmitBtn onClick={handleSubmit} disabled={!file || uploading}>
              {uploading ? (
                <>
                  <Spinner />
                  Importando...
                </>
              ) : (
                <>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M16 8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Importar
                </>
              )}
            </SubmitBtn>
          )}
        </Footer>
      </Sheet>
    </Overlay>
  );
};

export default ImportContactsModal;
